import { useQuery, useQueryClient, useMutation } from "react-query";
import API from "./utils/api";
import {
	PaymentInstitutionBranchDto,
	PaymentInstitutionBranchPaginator,
	CreateCompanyPaymentInstitutionBranchDto,
	QueryPaymentInstitutionBranchDto,
	UpdatePaymentInstitutionBranchDto,
	SearchPaymentInstitutionBranchDto,
} from "../models/payment-institution-branch.dto";
import ENDPOINTS from "./utils/endpoints";
import { toast } from "react-toastify";

export function useGetCompanyPaymentInstitutionBranch(
	companyId: number,
	options?: QueryPaymentInstitutionBranchDto,
) {
	const url =
		ENDPOINTS.BILLING_AND_SUBSCRIPTION.COMPANY_PAYMENT_INSTITUTION_BRANCH.GET;
	const request =
		API.BillingAndSubscriptionAPI.getCompanyPaymentInstitutionBranch(
			companyId,
			options,
		);

	const { data, isLoading, error, refetch } = useQuery<
		PaymentInstitutionBranchPaginator,
		Error
	>([url, companyId], () => request, { enabled: !!companyId });

	return { data, isLoading, error, refetch };
}

export function useGetCompanyPaymentInstitutionBranchSearch() {
	return useMutation(
		({
			companyId,
			...options
		}: { companyId: number } & SearchPaymentInstitutionBranchDto) =>
			API.BillingAndSubscriptionAPI.getCompanyPaymentInstitutionBranchSearch(
				companyId,
				options,
			),
		{
			onError: API.Handlers.onErrorHandler,
		},
	);
}

export function useGetCompanyPaymentInstitutionBranchById(
	companyId: number,
	id: number,
) {
	const url =
		ENDPOINTS.BILLING_AND_SUBSCRIPTION.COMPANY_PAYMENT_INSTITUTION_BRANCH
			.DETAIL;
	const request =
		API.BillingAndSubscriptionAPI.getCompanyPaymentInstitutionBranchById(
			id,
			companyId,
		);

	const { data, isLoading, error } = useQuery<
		{ data: PaymentInstitutionBranchDto },
		Error
	>([url, companyId, id], () => request, { enabled: !!id });

	return { data, isLoading, error };
}

export function useCreateCompanyPaymentInstitutionBranch() {
	const queryClient = useQueryClient();

	return useMutation(
		({
			companyId,
			payload,
		}: {
			companyId: number;
			payload: CreateCompanyPaymentInstitutionBranchDto;
		}) =>
			API.BillingAndSubscriptionAPI.createCompanyPaymentInstitutionBranch(
				companyId,
				payload,
			),
		{
			onSuccess: () => {
				toast.success("Record created successfully");
				queryClient.invalidateQueries(
					ENDPOINTS.BILLING_AND_SUBSCRIPTION.COMPANY_PAYMENT_INSTITUTION_BRANCH
						.GET,
				);
			},
			onError: API.Handlers.onErrorHandler,
		},
	);
}

export function useUpdateCompanyPaymentInstitutionBranch() {
	const queryClient = useQueryClient();

	return useMutation(
		({
			payload,
			id,
			companyId,
		}: {
			payload: UpdatePaymentInstitutionBranchDto;
			id: number;
			companyId: number;
		}) =>
			API.BillingAndSubscriptionAPI.updateCompanyPaymentInstitutionBranch(
				id,
				companyId,
				payload,
			),
		{
			onSuccess: () => {
				toast.success("Record updated successfully");
				queryClient.invalidateQueries(
					ENDPOINTS.BILLING_AND_SUBSCRIPTION.COMPANY_PAYMENT_INSTITUTION_BRANCH
						.GET,
				);
			},
			onError: API.Handlers.onErrorHandler,
		},
	);
}

export function useDeletePaymentInstitutionBranch() {
	return useMutation(
		({ id, companyId }: { id: number; companyId: number }) =>
			API.BillingAndSubscriptionAPI.deleteCompanyPaymentInstitutionBranch(
				id,
				companyId,
			),
		{
			onSuccess: () => {
				toast.success("Record deleted successfully");
			},
			onError: API.Handlers.onErrorHandler,
		},
	);
}

export function useUploadCompanyPaymentInstitutionBranches() {
	return useMutation(
		({ payload, companyId }: { payload: FormData; companyId: number }) =>
			API.BillingAndSubscriptionAPI.uploadCompanyPaymentInstitutionBranch(companyId, payload),
		{
			onError: API.Handlers.onErrorHandler,
		},
	);
}
